import React, { useEffect, useMemo, useState } from "react"
import { db } from "../../utils/firebase"
import { Plus } from "lucide-react"
import "./mailboxes.scss"

import Tile from "../../components/structure/tile/tile"
import Title from "../../components/structure/title/title"
import Table from "../../components/structure/table/table"
import Button from "../../components/ui/button/button"
import { LoadingIcon } from "../../utils/svgs"

import MailboxRow from "./_components/mailbox-row"
import AddMailbox from "./_components/add-mailbox"

/**
 * Admin page for managing the mailboxes that the communications-ingestion
 * pipeline subscribes to via Microsoft Graph.
 *
 * Realtime view of `monitored_mailboxes` — rows reflect the actual state of
 * Microsoft Graph subscriptions, including ACTIVE/PAUSED/ERROR status, the
 * timestamp of the last email received, and how long until the next renewal.
 *
 * No write paths from this page mutate Firestore directly. Adds, pauses,
 * resumes and removes all go through callable Cloud Functions in the
 * `functions-communications` codebase, which are responsible for keeping the
 * Graph subscription state in sync with the Firestore row.
 */
function Mailboxes() {
	const [mailboxes, setMailboxes] = useState([])
	const [adding, setAdding] = useState(false)
	const [loading, setLoading] = useState(true)

	useEffect(() => {
		// Server-side `display_name` ordering keeps reads cheap; we re-sort
		// client-side to make the comparison case-insensitive so "alex" and
		// "Alex" sort together.
		const unsubscribe = db
			.collection("monitored_mailboxes")
			.orderBy("display_name")
			.onSnapshot(
				(snapshot) => {
					const next = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
					setMailboxes(next)
					setLoading(false)
				},
				(err) => {
					console.error("Failed to subscribe to monitored_mailboxes", err)
					setLoading(false)
				}
			)

		return () => unsubscribe()
	}, [])

	const groupedMailboxes = useMemo(() => {
		const sortByName = (a, b) =>
			(a.display_name || a.email || "").localeCompare(b.display_name || b.email || "", undefined, {
				sensitivity: "base",
			})

		const shared = []
		const personal = []
		for (const mailbox of mailboxes) {
			if (isPersonalMailbox(mailbox.email)) {
				personal.push(mailbox)
			} else {
				shared.push(mailbox)
			}
		}

		return {
			shared: shared.sort(sortByName),
			personal: personal.sort(sortByName),
		}
	}, [mailboxes])

	return (
		<Tile fullPage={true}>
			<Title className="flex">
				<h1>Monitored mailboxes</h1>

				<div className="flex gap-2">
					<Button
						label="Add mailbox"
						small={true}
						icon={<Plus size={14} />}
						onClick={() => setAdding(true)}
					/>
				</div>
			</Title>

			<Table
				className="mailboxes-table"
				headings={["Name", "Email", "Status", "Last email", ""]}
				noResults={!loading && mailboxes.length === 0}
				noResultsMessage="No mailboxes yet">
				{groupedMailboxes.shared.map((mailbox) => (
					<MailboxRow
						key={mailbox.id}
						mailbox={mailbox}
					/>
				))}

				{groupedMailboxes.shared.length > 0 && groupedMailboxes.personal.length > 0 && (
					<tr className="mailboxes-divider-row">
						<td colSpan={5}>
							<span>Staff mailboxes</span>
						</td>
					</tr>
				)}

				{groupedMailboxes.personal.map((mailbox) => (
					<MailboxRow
						key={mailbox.id}
						mailbox={mailbox}
					/>
				))}

				{loading && (
					<div className="table-loading-splash">
						<div className="loading-wrapper">
							<p>Loading...</p>
							<div className="svg-loading-wrap">
								<LoadingIcon />
							</div>
						</div>
					</div>
				)}
			</Table>

			{adding && <AddMailbox close={() => setAdding(false)} />}
		</Tile>
	)
}

function isPersonalMailbox(email = "") {
	const localPart = email.split("@")[0] || ""
	return /^[a-z]+(?:[-'][a-z]+)?\.[a-z]+(?:[-'][a-z]+)?$/i.test(localPart)
}

export default Mailboxes
